import { useEffect, useMemo, useState } from "react";
import { resolveTenantScopeLabelsRemote } from "../governance/tenant-scope-labels.service.js";
import {
  deriveMyClubsFromBindings,
  enrichMyClubsWithTenantLabels,
} from "./hub-onboarding-state.js";

/**
 * Vereine aus role_bindings + menschenlesbare Namen via resolve_tenant_scope_labels.
 *
 * @param {Array|null|undefined} bindings
 * @param {(key: string, params?: object) => string} [t]
 * @returns {{ id: string, label: string, roles: string[] }[]|null}
 */
export function useMyClubsWithLabels(bindings, t) {
  const baseClubs = useMemo(() => {
    if (!Array.isArray(bindings) || !bindings.length) return null;
    return deriveMyClubsFromBindings(bindings);
  }, [bindings]);

  const scopeIdsKey = useMemo(() => {
    if (!baseClubs?.length) return "";
    return baseClubs
      .map((c) => String(c?.id || "").trim())
      .filter(Boolean)
      .sort()
      .join("|");
  }, [baseClubs]);

  const [tenantLabelsByScopeId, setTenantLabelsByScopeId] = useState({});

  useEffect(() => {
    if (!scopeIdsKey) {
      setTenantLabelsByScopeId({});
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const ids = scopeIdsKey.split("|").filter(Boolean);
        const res = await resolveTenantScopeLabelsRemote(ids);
        if (cancelled) return;
        if (res?.ok && res.byScopeId) {
          setTenantLabelsByScopeId(res.byScopeId);
        }
      } catch {
        /* RPC optional – Fallback bleibt */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [scopeIdsKey]);

  return useMemo(() => {
    if (!baseClubs) return null;
    return enrichMyClubsWithTenantLabels(baseClubs, tenantLabelsByScopeId, t);
  }, [baseClubs, tenantLabelsByScopeId, t]);
}
